import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";
import { useRecoilState } from "recoil";

import {
  canInsertStationRouteWaypoint,
  castErrorResult,
  createCancelledResult,
  createErrorResult,
  createSuccessResultVoid,
  editFormDataAF,
  errors,
  generateStationRouteWaypointId,
  generateStationRouteWaypointSecondaryId,
  getLastAddedStationArrivalTime,
  insertStationRouteWaypoint,
  IRideDto,
  IStationInfo,
  IStationRouteWaypointLocationDraft,
  RebuildRouteTypeEnum,
  resolveStationRouteWaypointAreaId,
  resolveStationRouteWaypointInsertAfterIndex,
  resolveStationRouteWaypointName,
  RideCrudModeKeyTuple,
  SimpleResult,
  toWaypointMapStations,
  WaypointCategoryEnum,
} from "@ryde/shared";

import { useRebuildRoute } from "./use-rebuild-route";
import { useRideMlPathWaypointConfirm } from "./use-ride-ml-path-waypoint-confirm";

export const useRideWaypointActions = () => {
  const { t } = useTranslation();
  const [rideDto] = useRecoilState<Partial<IRideDto> | undefined>(
    editFormDataAF(RideCrudModeKeyTuple),
  );
  const { rebuildRideRoute } = useRebuildRoute();
  const { confirmWaypointChangeWithSavedMlPath } =
    useRideMlPathWaypointConfirm();

  const stations = useMemo(
    () => toWaypointMapStations(rideDto?.metadata?.stationsInfo ?? []),
    [rideDto?.metadata?.stationsInfo],
  );

  const addWaypointFromDraft = useEvent(
    async (
      draft: IStationRouteWaypointLocationDraft,
    ): Promise<SimpleResult<void>> => {
      const rideStations = rideDto?.metadata?.stationsInfo ?? [];

      if (!rideStations.length || !rideDto) {
        return createErrorResult(
          errors.common.technicalProblemOccurred,
          "Cannot add waypoint",
        );
      }

      if (!canInsertStationRouteWaypoint(rideStations)) {
        return createErrorResult(
          errors.common.technicalProblemOccurred,
          "Cannot add waypoint",
        );
      }

      const confirmResult = await confirmWaypointChangeWithSavedMlPath(rideDto);

      if (!confirmResult.confirmed) {
        return createCancelledResult();
      }

      const rideForWaypointChange: Partial<IRideDto> =
        confirmResult.nextMetadata
          ? { ...rideDto, metadata: confirmResult.nextMetadata }
          : rideDto;
      const rideStationsAfterConfirm =
        rideForWaypointChange.metadata?.stationsInfo ?? rideStations;

      const insertAfterIndex =
        draft.insertAfterIndex ??
        resolveStationRouteWaypointInsertAfterIndex({
          stations: rideStationsAfterConfirm,
          latitude: draft.latitude,
          longitude: draft.longitude,
          routePolyline: rideForWaypointChange.metadata?.routePolyline,
        });

      const neighborStation = rideStationsAfterConfirm[insertAfterIndex];
      const areaId = await resolveStationRouteWaypointAreaId({
        latitude: draft.latitude,
        longitude: draft.longitude,
        stations: rideStationsAfterConfirm,
        insertAfterIndex,
      });
      const waypoint: IStationInfo = {
        id: generateStationRouteWaypointId(),
        name: resolveStationRouteWaypointName({
          customName: draft.customName,
          fullAddress: draft.fullAddress,
          fallbackName: t("stationRoute.waypoint.DEFAULT_NAME"),
        }),
        locationInfo: {
          latitude: draft.latitude,
          longitude: draft.longitude,
          fullAddress: draft.fullAddress,
        },
        waypointCategory: WaypointCategoryEnum.RideWaypoint,
        areaId,
        secondaryId: generateStationRouteWaypointSecondaryId(),
        arrivalTime:
          neighborStation?.arrivalTime ??
          getLastAddedStationArrivalTime(rideForWaypointChange) ??
          rideForWaypointChange.rideStart,
      };

      const stationsUpdated = insertStationRouteWaypoint({
        stations: rideStationsAfterConfirm,
        waypoint,
        insertAfterIndex,
      });

      const rideWithNewStations: Partial<IRideDto> = {
        ...rideForWaypointChange,
        metadata: {
          ...rideForWaypointChange.metadata,
          stationsInfo: stationsUpdated,
        },
      };

      const rebuildResult = await rebuildRideRoute(rideWithNewStations, {
        rebuildType: RebuildRouteTypeEnum.KeepOrder,
        optimize: false,
      });

      if (!rebuildResult.success) {
        return castErrorResult(rebuildResult);
      }

      return createSuccessResultVoid();
    },
  );

  return {
    addWaypointFromDraft,
    rideDto,
    stations,
  };
};
